import type {
  TIsoDate,
} from './date.ts';
import {
  isSemanticVersion,
} from './version.ts';

/** E.g. `## [v2.4.26] - 2026-10-13`. Older headings have no `v`, `## [Unreleased]` has no version. */
const RELEASE_HEADING_PATTERN = /^## \[v?([^\]]+)\](?: - (\d{4}-\d{2}-\d{2}))?/;

export type TChangelogRelease = {
  version: string,
  releaseDate?: TIsoDate,
  /** The markdown below the heading of the release, up to the next release. */
  releaseNotes: string,
};

/**
 * Splits a changelog that follows Keep a Changelog, https://keepachangelog.com/en/1.1.0/, into its
 * released versions, in file order. Every release starts with a heading like
 * `## [1.2.3] - 2026-10-13`, optionally with a leading `v`; the version must be a semantic version.
 * The `[Unreleased]` section and headings without a valid version are left out.
 */
export function parseChangelog(changelog: string): TChangelogRelease[] {
  const releases: TChangelogRelease[] = [];
  let releaseNotesLines: string[] | undefined;

  function finishRelease() {
    if (releaseNotesLines !== undefined) {
      releases[releases.length - 1].releaseNotes = releaseNotesLines.join('\n').trim();
    }
    releaseNotesLines = undefined;
  }

  changelog.split('\n').forEach((line) => {
    if (!line.startsWith('## ')) {
      releaseNotesLines?.push(line);
      return;
    }
    finishRelease();
    const match = RELEASE_HEADING_PATTERN.exec(line);
    if (match === null || !isSemanticVersion(match[1])) {
      return;
    }
    const [
      ,
      version,
      releaseDate,
    ] = match;
    releases.push({
      version,
      releaseDate: releaseDate as TIsoDate | undefined,
      releaseNotes: '',
    });
    releaseNotesLines = [];
  });
  finishRelease();

  return releases;
}
